import { Component, Vue } from "vue-property-decorator";
import {
    BrbpPprWrapper,
    BrbpWrapper,
    IBrbpWrapper
} from '@/modules/budget-request/components/js/brbp-wrapper';
import {
    buildRowHierarchy,
    collectRowNumbers,
    mergeSeeWithDri,
    sortDriRowsByRegionAndId
} from '@/modules/budget-request/components/js/row-hierarchy';
import BudgerRequestMixin from './bp-mixin';

interface EmptyRequiredFields {
    isRegionFilled?: Boolean,
    isBipFilled?: Boolean,
    isProjectFilled?: Boolean,
    isAnyMoneyFieldFilled?: Boolean
}

interface EmptyRequiredFieldsDri extends EmptyRequiredFields {
    isIndicatorFilled?: Boolean,
    isFinalResultFilled?: Boolean
}

interface EmptyRequiredFieldsSee extends EmptyRequiredFields {
    isEffectFilled?: Boolean
}

type RowKind = 'dri' | 'see';

interface RowNumbers {
    dri: Map<any, string>;
    see: Map<any, string>;
}

@Component
export default class BudgerRequestDataValidationMixin extends BudgerRequestMixin {
    public brbpWrapper: BrbpWrapper = new BrbpWrapper();
    public curFinalResults: any[] = [];
    public regions: any[] = [];
    public filter: any = [];

    //  ==================== Валидация данных ==========================
    private validationMsgs: string[] = [];
    public ifAllRequestedFieldsFilled(): any {
        this.validationMsgs.splice(0);
        this.fillValidationMsgsMain(this.validationMsgs);
        
        // '000' (нулевая подпрограмма) хранится на brbpWrapper напрямую, поэтому проверяем и его, и все pprData вместе
        const allSubProgWrappers: IBrbpWrapper[] = [this.brbpWrapper, ...(this.brbpWrapper.pprData || [])]
            .sort((a, b) => Number(a.brbp?.ppr || 0) - Number(b.brbp?.ppr || 0));
        allSubProgWrappers.forEach(ppr => {
            // номера считаем до проверки: она удаляет пустые строки ПЭЭ, а в таблице они нумеруются
            const rowNumbers = this.getRowNumbers(ppr);
            if (!this.checkIfPprFieldsFilled(ppr)) {
                this.fillValidationMsgs(ppr, rowNumbers);
            }
        });

        if (this.validationMsgs.length > 0) {
            this.showValidationToast('warning', this.$t('modules.budget_request.budget_programs.validation.check_data') as string, this.validationMsgs);
            return false;
        }

        return true;
    }

    public fillValidationMsgsMain(validationMsgs: string[]) {

    }

    private checkIfPprFieldsFilled(ppr: any): boolean {
        let isCheckPassed = true;

        isCheckPassed = this.isDriDataPassedValidation(ppr, isCheckPassed);
        isCheckPassed = this.isSeeDataPassedValidation(ppr, isCheckPassed);

        return isCheckPassed;
    }   

    public isDriDataPassedValidation(ppr: BrbpPprWrapper, isCheckPassed: boolean): boolean {
        if (ppr.driData?.length > 0) {
            const isPprByRegion = ppr.brbp?.driByRegion;
            
            ppr.driData.forEach((dri: any) => {
                const emptyRequiredFieldsDri: EmptyRequiredFieldsDri = {};
                this.$set(emptyRequiredFieldsDri, 'isRegionFilled', !(isPprByRegion && !dri.region));
                
                if (this.isBipProgram) {
                    this.$set(emptyRequiredFieldsDri, 'isBipFilled', !!(dri.bip));
                } else {
                    this.$set(emptyRequiredFieldsDri, 'isProjectFilled', !!(dri.project && dri.project.id));
                }

                this.$set(emptyRequiredFieldsDri, 'isIndicatorFilled', !!(dri.indicator && dri.indicator.id));

                const hasFinalResults = this.curFinalResults.length > 0;
                this.$set(emptyRequiredFieldsDri, 'isFinalResultFilled', !hasFinalResults || !!dri.finalResult);

                this.$set(emptyRequiredFieldsDri, 'isAnyMoneyFieldFilled', !!(dri.reportYear && Number(dri.reportYear) > 0)
                                                                        || !!(dri.approvedPlan && Number(dri.approvedPlan) > 0)
                                                                        || !!(dri.correctPlan && Number(dri.correctPlan) > 0)
                                                                        || !!(dri.planPeriod1 && Number(dri.planPeriod1) > 0)
                                                                        || !!(dri.planPeriod2 && Number(dri.planPeriod2) > 0)
                                                                        || !!(dri.planPeriod3 && Number(dri.planPeriod3) > 0));
                
                this.$set(dri, 'validationResult', emptyRequiredFieldsDri);
                if (isCheckPassed) {
                    isCheckPassed = this.getIsCheckPassed(emptyRequiredFieldsDri);
                }
            })
        }

        return isCheckPassed;
    }

    public isSeeDataPassedValidation(ppr: BrbpPprWrapper, isCheckPassed: boolean, isEmptyRowShouldBeDeleted = true): boolean {
        if (ppr.seeData?.length > 0) {
            const isPprByRegion = ppr.brbp?.driByRegion;
            for (let i = ppr.seeData.length - 1; i >= 0; i--) {
                const see = ppr.seeData[i];
                const emptyRequiredFieldsSee:EmptyRequiredFieldsSee = {};

                this.$set(emptyRequiredFieldsSee, 'isRegionFilled', !(isPprByRegion && !see.region));
                
                if (this.isBipProgram) {
                    this.$set(emptyRequiredFieldsSee, 'isBipFilled', !!(see.bip));
                } else {
                    this.$set(emptyRequiredFieldsSee, 'isProjectFilled', !!(see.project && see.project.id));
                }

                this.$set(emptyRequiredFieldsSee, 'isEffectFilled', !!(see.see && see.see.id));
 
                this.$set(emptyRequiredFieldsSee, 'isAnyMoneyFieldFilled', !!(see.effPlanPeriod1 && Number(see.effPlanPeriod1) > 0)
                                                                        || !!(see.effPlanPeriod2 && Number(see.effPlanPeriod2) > 0)
                                                                        || !!(see.effPlanPeriod3 && Number(see.effPlanPeriod3) > 0));
                
                const isRowEmpty =  !emptyRequiredFieldsSee.isEffectFilled && !emptyRequiredFieldsSee.isAnyMoneyFieldFilled;
                    
                if (isEmptyRowShouldBeDeleted && isRowEmpty) {
                    ppr.seeData.splice(i, 1);
                } else {
                    this.$set(see, 'validationResult', emptyRequiredFieldsSee);

                    if (!isRowEmpty && isCheckPassed) {
                        isCheckPassed = this.getIsCheckPassed(emptyRequiredFieldsSee);
                    }
                }
                
            }
        }

        return isCheckPassed;
    }

    private fillValidationMsgs(ppr: any, rowNumbers: RowNumbers): void {
        const pprCode = ppr.brbp?.ppr;
        const isPprByRegion = !!ppr.brbp?.driByRegion;
        const subProgram = !pprCode || String(pprCode) === '000' ? '' : this.getSubProgramLabel(pprCode);

        const addMessage = (kind: RowKind, row: any) => {
            const rowNumber = rowNumbers[kind].get(row);
            const errMessage = this.errorMessageBuild({ data: row, kind, isPprByRegion, subProgram, rowNumber });
            if (errMessage) this.validationMsgs.push(errMessage);
        };

        const byRowNumber = (kind: RowKind) => (a: any, b: any) =>
            this.compareRowNumbers(rowNumbers[kind].get(a), rowNumbers[kind].get(b));

        [...(ppr.driData || [])].sort(byRowNumber('dri')).forEach((dri: any) => addMessage('dri', dri));
        [...(ppr.seeData || [])].sort(byRowNumber('see')).forEach((see: any) => addMessage('see', see));
    }

    // строки без номера уходят в конец
    private compareRowNumbers(a?: string, b?: string): number {
        if (a === undefined || b === undefined) return Number(a === undefined) - Number(b === undefined);
        const aParts = a.split('.').map(Number);
        const bParts = b.split('.').map(Number);
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
            const diff = (aParts[i] ?? -1) - (bParts[i] ?? -1);
            if (diff) return diff;
        }
        return 0;
    }

    private errorMessageBuild({ data, kind, isPprByRegion, subProgram, rowNumber }: {
        data: any; kind: RowKind; isPprByRegion: boolean; subProgram: string; rowNumber?: string
    }): string {
        const valRes = data.validationResult;
        if (!valRes || !Object.values(valRes).some(value => value === false)) return '';

        const isDri = kind === 'dri';
        const columns = [
            { isActual: isPprByRegion, isFilled: valRes.isRegionFilled, label: 'table.region' },
            { isActual: true, isFilled: this.isBipProgram ? valRes.isBipFilled : valRes.isProjectFilled, label: 'table.project' },
            ...(isDri ? [
                { isActual: true, isFilled: valRes.isIndicatorFilled, label: 'table.indicator' },
                { isActual: this.curFinalResults.length > 0, isFilled: valRes.isFinalResultFilled, label: 'table.final_result' }
            ] : [
                { isActual: true, isFilled: valRes.isEffectFilled, label: 'effect.effect' }
            ])
        ].filter(column => column.isActual);

        const notFilled = columns.filter(column => column.isFilled === false).map(column => `«${this.bpText(column.label)}»`);
        const hasNoPositiveValue = valRes.isAnyMoneyFieldFilled === false;
        const isRowEmpty = isDri && hasNoPositiveValue && notFilled.length === columns.length;

        const table = this.bpText(isDri ? 'card.table_ppr' : 'card.table_eff');
        // у строки ПЭЭ без пары в ППР номера нет: в таблице она не показывается
        let rowTitle = table;
        if (rowNumber) {
            rowTitle = subProgram
                ? this.bpText('validation.row_prefix', { ppr: subProgram, table, num: rowNumber })
                : this.bpText('validation.row_prefix_no_ppr', { table, num: rowNumber });
        } else if (subProgram) {
            rowTitle = this.bpText('validation.row_prefix_no_num', { ppr: subProgram, table });
        }

        const problems: string[] = [];
        if (isRowEmpty) {
            problems.push(this.bpText('validation.row_empty'));
        } else {
            if (notFilled.length > 0) problems.push(this.bpText('validation.not_filled', { fields: notFilled.join(', ') }));
            if (hasNoPositiveValue) problems.push(this.bpText('validation.no_positive_value'));
        }

        return problems.length > 0 ? `${rowTitle} — ${problems.join('; ')}` : '';
    }

    private bpText(key: string, params?: any): string {
        return this.$t(`modules.budget_request.budget_programs.${key}`, params) as string;
    }

    public getSubProgramLabel(pprCode: any): string {
        return String(pprCode);
    }

    public defaultDriByRegion(): boolean {
        return false;
    }

    private getRowNumbers(ppr: IBrbpWrapper): RowNumbers {
        const shown = this.getShownRowNumbers(ppr);
        const calculated = this.calcRowNumbers(ppr);
        const resolve = (rows: any[], shownNumbers: Map<any, string> | undefined, calculatedNumbers: Map<any, string>) => {
            const res = new Map<any, string>();
            rows.forEach((row: any) => {
                const rowNumber = shownNumbers?.get(row.id) ?? calculatedNumbers.get(row);
                if (rowNumber !== undefined) res.set(row, rowNumber);
            });
            return res;
        };
        return {
            dri: resolve(ppr.driData || [], shown?.dri, calculated.dri),
            see: resolve(ppr.seeData || [], shown?.see, calculated.see)
        };
    }

    // Таблицы не пересортировываются после правок (новые строки), поэтому для открытой БПП берём номера с экрана
    private getShownRowNumbers(ppr: IBrbpWrapper): RowNumbers | null {
        const table = this.$refs.driEditModule as (Vue & { getRowNumbers: () => RowNumbers }) | undefined;
        return table && table.$props.data === ppr ? table.getRowNumbers() : null;
    }

    private calcRowNumbers(ppr: IBrbpWrapper): RowNumbers {
        const driByRegion = ppr.brbp?.driByRegion;
        const ifRegion = typeof driByRegion === 'boolean' ? driByRegion : this.defaultDriByRegion();
        const driRows = sortDriRowsByRegionAndId([...(ppr.driData || [])]);
        const toHierarchyRows = (rows: any[], projectField: string) => rows.map((row: any) => ({
            ...(this.isBipProgram && row.bip ? { ...row, [projectField]: { id: row.bip } } : row),
            rowSource: row
        }));

        const dri = collectRowNumbers(buildRowHierarchy(toHierarchyRows(driRows, 'project'), 'dri', ifRegion), 'rowSource');

        const seeRows = mergeSeeWithDri(
            ppr.seeData || [],
            driRows,
            (item: any, row: any) => this.comparePrjOrBip(item, row, { brbp: ppr.brbp || {} }, this.filter.bpState),
            (driRow: any) => ({ region: driRow.region, bip: driRow.bip, project: driRow.project })
        );
        const seeProjectField = this.isBipProgram ? 'bipObj' : 'project';
        const see = collectRowNumbers(buildRowHierarchy(toHierarchyRows(seeRows, seeProjectField), 'see', ifRegion, seeProjectField), 'rowSource');

        return { dri, see };
    }

    private showValidationToast(variant: any, title: string, tostbody: any) {
        const h = this.$createElement;
        const id = 'bp-validation-toast';
        const msgs: any[] = [];

        tostbody.forEach((msg: string) => {
           msgs.push(h('p', {}, msg))
        })

        this.$bvToast.hide(id);
        this.$bvToast.toast(msgs, {
          id: id,
          title: title,
          variant: variant,
          toaster: 'b-toaster-top-center',
          noAutoHide: true
        })

        this.unbindToastOutsideClick();
        // beforeDestroy в миксине конфликтует с одноимённым методом компонента
        this.$off('hook:beforeDestroy', this.unbindToastOutsideClick);
        this.$once('hook:beforeDestroy', this.unbindToastOutsideClick);
        // откладываем, чтобы не поймать клик, которым запустили сохранение
        this.toastBindTimer = setTimeout(() => document.addEventListener('click', this.onOutsideToastClick, true));
    }

    private toastBindTimer?: ReturnType<typeof setTimeout>;

    private onOutsideToastClick(event: Event) {
        const target = event.target as HTMLElement | null;
        if (target?.closest('#bp-validation-toast, #bp-validation-toast__toast_outer')) return;
        this.$bvToast.hide('bp-validation-toast');
        this.unbindToastOutsideClick();
    }

    private unbindToastOutsideClick() {
        clearTimeout(this.toastBindTimer);
        document.removeEventListener('click', this.onOutsideToastClick, true);
    }

    private getIsCheckPassed(fields: any) {
        for (const check in fields) {
            const checkFailed = !fields[check]
            if (checkFailed) return false;
        }
        return true;
    }
    //  ==================== Валидация данных ==========================

    private get isBipProgram(): boolean {
        return this.filter.bpState === 1
    }

    public get lng(): String {
        let lng = 'ru';
        if (this.$i18n.locale === 'kk') lng = 'kk';
        return lng;
    }

    public locName(obj: any, kkField = 'name_kk'): string {
        return obj ? (this.$i18n.locale === 'kk' && obj[kkField]) || obj.name_ru : '';
    }

    


}